import { useState, useEffect } from "react";
import { MotifButton, MotifProgressLoader } from "@/libs/@ey-xd/motif-react";
import { useTranslation } from "react-i18next";

import StageTile from "./StageTile";
import CreateEditStage from "./CreateEditStage";
import StatusTile from "./StatusTile";
import CreateEditStatus from "./CreateEditStatus";
import { StageType, EmptyStage, EmptyStatus, OrderChange } from "./StagesType";
import {
  useLazyGetStagesQuery,
  useUpdateStageMutation,
  useUpdateStatusMutation,
} from "@/services/stages/stagesReducer";
import "./Stages.css";
import { useCase } from "@/features/case/use-case";
import { useSelector } from "react-redux";
import { havingAddPermission } from "@/utility/common";
import { CREATE_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";

export default function StageManagement() {
  const { t } = useTranslation();
  const [stagesData, setStagesData] = useState<StageType[]>([]);
  const [filteredStatuses, setFilteredStatuses] = useState<any[]>([]);
  let [interval, setInterval] = useState<any>(null);
  const [showCreateStage, setShowCreateStage] = useState(false);
  const [showCreateStatus, setShowCreateStatus] = useState(false);
  const [addStatusButtonDisable, setAddStatusButtonDisable] = useState(false);
  const caseData = useCase();
  const [selectedStage, setSelectedStage] = useState("");
  const [getStagesQuery] = useLazyGetStagesQuery();
  const [updateStageQuery] = useUpdateStageMutation();
  const [updateStatusQuery] = useUpdateStatusMutation();
  const permissions = useSelector((state: any) => state.permissions);
  const { ClaimStageDefinition, ClaimStatusDefinition } = permissions;
  const [abortController, setAbortController] = useState<any>();
  const getStagesData = async (isCreate: boolean) => {
    let data = getStagesQuery(caseData.caseId);
    setAbortController(data);
    let response = await data;
    if (response.data && response.data.length !== 0) {
      if (response?.status === "rejected") return;
      setAddStatusButtonDisable(true);
      const stages = [...response.data].sort(
        (a: StageType, b: StageType) => a.sortOrder - b.sortOrder
      );
      setStagesData(stages);
      if (selectedStage === "") {
        setSelectedStage(stages[0].id);
      }
      if (isCreate) {
        updateStageOrder(stages);
        setSelectedStage(stages[0].id);
      }
    } else {
      setStagesData([]);
      setFilteredStatuses([]);
      setAddStatusButtonDisable(false);
    }
  };
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = useState<any>(
    {
      message: "",
      show: false,
      type: "",
      index: "",
      variant: "",
    }
  );
  const [showToast, setShowToast] = useState<any>({
    type: "",
    message: "",
    show: false,
    variant: "",
  });
  const handleStageOrderChange = (change: OrderChange, id: string) => {
    const index = stagesData.findIndex((x) => x.id === id);
    if (change === OrderChange.Up && index !== 0) {
      const tempData = [...stagesData];
      tempData.splice(index - 1, 0, tempData.splice(index, 1)[0]);
      setStagesData([...tempData]);
      updateStageOrder(tempData);
    } else if (change === OrderChange.Down && index !== stagesData.length - 1) {
      const tempData = [...stagesData];
      tempData.splice(index + 1, 0, tempData.splice(index, 1)[0]);
      setStagesData([...tempData]);
      updateStageOrder(tempData);
    }
  };

  const handleStatusOrderChange = (change: OrderChange, id: string) => {
    const index = filteredStatuses.findIndex((x) => x.id === id);
    if (change === OrderChange.Up && index !== 0) {
      const tempData = [...filteredStatuses];
      tempData.splice(index - 1, 0, tempData.splice(index, 1)[0]);
      setFilteredStatuses([...tempData]);
      updateStatusOrder(tempData);
    } else if (
      change === OrderChange.Down &&
      index !== filteredStatuses.length - 1
    ) {
      const tempData = [...filteredStatuses];
      tempData.splice(index + 1, 0, tempData.splice(index, 1)[0]);
      setFilteredStatuses([...tempData]);

      updateStatusOrder(tempData);
    }
  };

  const updateStageOrder = (tempData: any) => {
    tempData.map(async (stage: any, index: number) => {
      const requestBody = { ...stage, sortOrder: index + 1 };
      await updateStageQuery(requestBody);
    });
  };

  const updateStatusOrder = (tempData: any) => {
    tempData.map(async (status: any, index: number) => {
      const requestBody = { ...status, sortOrder: index + 1 };
      await updateStatusQuery(requestBody).then(() => {
        getStagesData(false);
      });
    });
  };

  const filterStatuses = () => {
    if (stagesData) {
      const tempData: any = [...stagesData].filter((stage) => {
        return stage.id === selectedStage;
      });

      if (tempData.length !== 0) {
        const statuses = [...tempData[0].claimStatuses].sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );
        setFilteredStatuses(statuses);
      }
    }
  };

  useEffect(() => {
    getStagesData(false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    filterStatuses();
    // eslint-disable-next-line
  }, [selectedStage, stagesData]);

  return (
    <>
      {!stagesData ? (
        <MotifProgressLoader show variant="default" />
      ) : (
        <>
          <div className="stage-management-container">
            <div className="motif-body1-default-regular stage-management-title">
              {t("pages.configuration.stages.subtitle")}
            </div>
            <div className="stage-management-body" style={{ height: "1380px" }}>
              <div className="stages-container">
                <div className="stages-header motif-row mar-r-10 mar-l-10">
                  <div className="motif-body1-default-bold ">
                    {t("pages.configuration.stages.title")}
                  </div>
                  {havingAddPermission(ClaimStageDefinition) && (
                    <MotifButton
                      onClick={() => {
                        setShowDeleteSuccessMessage({
                          type: "",
                          message: "",
                          show: false,
                          index: "",
                          variant: "",
                        });
                        setShowToast({
                          type: "",
                          message: "",
                          show: false,
                          variant: "",
                        });
                        setShowCreateStage(true);
                      }}
                      size="small"
                      type="submit"
                      variant={CREATE_BUTTON_VARIANT}
                    >
                      {t("pages.configuration.stages.newStage")}{" "}
                    </MotifButton>
                  )}
                </div>
                {showToast.show && showToast.variant === "stage" && (
                  <NotificationGrid
                    show={true}
                    message={showToast.message}
                    type={showToast.type}
                    scrollToTop={false}
                    onClose={() => {
                      setShowToast({
                        type: "",
                        message: "",
                        show: false,
                        variant: "",
                      });
                    }}
                  />
                )}
                <div
                  style={{ height: "90%", overflow: "auto" }}
                  className="stages-body"
                >
                  {stagesData.map((stage: StageType, index: number) => {
                    return (
                      <StageTile
                        ClaimStageDefinition={ClaimStageDefinition}
                        stageData={stage}
                        selectedStage={selectedStage}
                        setSelectedStage={setSelectedStage}
                        getStageData={getStagesData}
                        handleOrderChange={handleStageOrderChange}
                        key={stage.id}
                        setShowToast={setShowToast}
                        setShowDeleteSuccessMessage={
                          setShowDeleteSuccessMessage
                        }
                        showDeleteSuccessMessage={showDeleteSuccessMessage}
                        index={index}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="status-container">
                <div className="status-header motif-row mar-l-10 mar-r-10">
                  <div className="motif-body1-default-bold mar-b-5">
                    {t("pages.configuration.stages.status")}
                  </div>
                  {havingAddPermission(ClaimStatusDefinition) && (
                    <MotifButton
                      onClick={() => {
                        setShowCreateStatus(true);

                        setShowDeleteSuccessMessage({
                          type: "",
                          message: "",
                          show: false,
                          index: "",
                          variant: "",
                        });
                        setShowToast({
                          type: "",
                          message: "",
                          show: false,
                          variant: "",
                        });
                      }}
                      size="small"
                      type="submit"
                      variant={CREATE_BUTTON_VARIANT}
                      disabled={!addStatusButtonDisable}
                    >
                      {t("pages.configuration.stages.newStatus")}{" "}
                    </MotifButton>
                  )}
                </div>
                {showToast.show && showToast.variant === "status" && (
                  <NotificationGrid
                    show={true}
                    message={showToast.message}
                    type={showToast.type}
                    scrollToTop={false}
                    onClose={() => {
                      setShowToast({
                        type: "",
                        message: "",
                        show: false,
                        variant: "",
                      });
                    }}
                  />
                )}
                <div
                  style={{ height: "88%", overflowX: "auto" }}
                  className="status-body "
                >
                  {filteredStatuses.map((status, index) => {
                    return (
                      <StatusTile
                        ClaimStatusDefinition={ClaimStatusDefinition}
                        statusData={status}
                        getStageData={getStagesData}
                        handleOrderChange={handleStatusOrderChange}
                        numStatuses={filteredStatuses.length}
                        setFilteredStatuses={setFilteredStatuses}
                        filteredStatuses={filteredStatuses}
                        selectedStage={selectedStage}
                        setSelectedStage={setSelectedStage}
                        key={status.id}
                        index={index}
                        interval={interval}
                        setInterval={setInterval}
                        abortController={abortController}
                        setShowToast={setShowToast}
                        setShowDeleteSuccessMessage={
                          setShowDeleteSuccessMessage
                        }
                        showDeleteSuccessMessage={showDeleteSuccessMessage}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <CreateEditStage
        ClaimStageDefinition={ClaimStageDefinition}
        show={showCreateStage}
        setShow={setShowCreateStage}
        stageData={{ ...EmptyStage, caseId: caseData.caseId }}
        isEdit={false}
        getStageData={getStagesData}
        setShowToast={setShowToast}
      />

      <CreateEditStatus
        ClaimStatusDefinition={ClaimStatusDefinition}
        show={showCreateStatus}
        setShow={setShowCreateStatus}
        statusData={{
          ...EmptyStatus,
          caseId: caseData.caseId,
          claimStageDefinitionId: selectedStage,
        }}
        isEdit={false}
        getStageData={getStagesData}
        numStatuses={filteredStatuses.length}
        setShowToast={setShowToast}
      />
    </>
  );
}
